"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { API_ENDPOINTS } from "@/constants/config";
import { v4 as uuidv4 } from "uuid";
import { useCitySearch } from "./useCitySearch";
import { useChatHistory } from "./useChatHistory";

const STEPS = ["welcome", "city", "schoolName", "phone", "name", "designation", "strength", "usingErp", "submit"];

const STRENGTH_OPTIONS = [
  { value: "1-500", label: "1 - 500" },
  { value: "501-1000", label: "501 - 1000" },
  { value: "1001-2000", label: "1001 - 2000" },
  { value: "2000+", label: "2000+" },
];

const DESIGNATION_OPTIONS = [
  { value: "Principal", label: "Principal" },
  { value: "Admin", label: "School Admin" },
  { value: "Owner", label: "Owner / Management" },
  { value: "Teacher", label: "Teacher" },
];

const WELCOME_MSG =
  "Hi! I'm here to help you learn about our School ERP system. How would you like to proceed?";

const SUGGESTED_QUESTIONS = [
  "What features does Clynox offer?",
  "How much does it cost?",
  "Is there a free trial?",
  "How does bus tracking work?",
  "Can I import existing data?",
  "What support is available?"
];

const QUICK_REPLIES = [
  { label: "💰 Pricing", query: "What are the pricing plans?" },
  { label: "✨ Features", query: "Tell me about key features" },
  { label: "📅 Demo", query: "How can I get a demo?" },
  { label: "💬 Support", query: "What support do you provide?" }
];

export default function ERPLeadBot() {
  const [visible, setVisible] = useState(false);
  const [open, setOpen] = useState(false);
  const [teaserVisible, setTeaserVisible] = useState(false);
  const [mode, setMode] = useState("lead_flow");
  const [stepIndex, setStepIndex] = useState(0);
  const [messages, setMessages] = useState([{ role: "bot", text: WELCOME_MSG }]);
  const [loading, setLoading] = useState(false);
  const messagesRef = useRef(null);
  const [cities, setCities] = useState([]);
  const [citiesLoading, setCitiesLoading] = useState(false);
  const [formData, setFormData] = useState({
    city: "",
    schoolName: "",
    phoneNumber: "",
    contactName: "",
    designation: "",
    strengthOfSchool: "",
    isCurrentlyUsingErp: null,
  });
  const [submitStatus, setSubmitStatus] = useState(null);
  const [chatInput, setChatInput] = useState("");
  const [leadSessionId, setLeadSessionId] = useState("");
  const [escalation, setEscalation] = useState(null); // { type: "callback"|"demo", question?: string } | null
  const dropdownRef = useRef(null);
  const [showAllSuggestions, setShowAllSuggestions] = useState(false);
  const [showLeadCapturePrompt, setShowLeadCapturePrompt] = useState(false);
  const [freeChatUserMessageCount, setFreeChatUserMessageCount] = useState(0);

  // Use city search hook
  const { searchQuery, setSearchQuery, filteredCities, showDropdown, setShowDropdown } = useCitySearch(cities);
  
  // Use chat history hook
  const { getConversationContext } = useChatHistory(messages);

 
  useEffect(() => {
    const storageKey = "clynox_erp_bot_visible";
    const alreadyVisible = window.localStorage.getItem(storageKey) === "1";
    if (alreadyVisible) {
      setVisible(true);
      setOpen(false);
      setTeaserVisible(true);
      return;
    }

    const t = setTimeout(() => {
      setVisible(true);
      setOpen(true);
      setTeaserVisible(false);
      window.localStorage.setItem(storageKey, "1");
    }, 5000);

    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const openFromAnywhere = () => {
      window.localStorage.setItem("clynox_erp_bot_visible", "1");
      setVisible(true);
      setTeaserVisible(false);
      setOpen(true);
    };

    window.addEventListener("clynox-open-erp-bot", openFromAnywhere);
    return () => window.removeEventListener("clynox-open-erp-bot", openFromAnywhere);
  }, []);

  useEffect(() => {
    if (!open) return;
    const original = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = original;
    };
  }, [open]);

  useEffect(() => {
    const key = "clynox_erp_lead_session_id";
    const existing = window.localStorage.getItem(key);
    if (existing) {
      setLeadSessionId(existing);
      return;
    }
    const id = uuidv4();
    window.localStorage.setItem(key, id);
    setLeadSessionId(id);
  }, []);

  const rotateLeadSession = useCallback(() => {
    const key = "clynox_erp_lead_session_id";
    const id = uuidv4();
    window.localStorage.setItem(key, id);
    setLeadSessionId(id);
    setFormData({
      city: "",
      schoolName: "",
      phoneNumber: "",
      contactName: "",
      designation: "",
      strengthOfSchool: "",
      isCurrentlyUsingErp: null,
    });
    setStepIndex(STEPS.indexOf("city"));
    setEscalation(null);
    setSubmitStatus(null);
  }, []);

  const upsertLead = useCallback(
    async (patch) => {
      if (!leadSessionId) return;
      try {
        await fetch(API_ENDPOINTS.publicLeadUpsert, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ leadSessionId, ...patch }),
        });
      } catch {}
    },
    [leadSessionId]
  );

  const leadAction = useCallback(
    async (action, patch) => {
      if (!leadSessionId) return;
      const isSubmit = action === "submit";
      const actionLeadSessionId = isSubmit ? leadSessionId : uuidv4();
      const body = {
        leadSessionId: actionLeadSessionId,
        action,
        ...patch,
      };
      if (!isSubmit) body.cloneFromLeadSessionId = leadSessionId;
      try {
        await fetch(API_ENDPOINTS.publicLeadAction, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        });
      } catch {}
    },
    [leadSessionId]
  );

 
  useEffect(() => {
    setCitiesLoading(true);
    fetch(API_ENDPOINTS.chatbotCities)
      .then((res) => res.json())
      .then((d) => {
        const list = d?.data?.cities || [];
        const arr = Array.isArray(list) ? list.slice() : [];
        arr.sort((a, b) => {
          const an = (a.name || "").toLowerCase();
          const bn = (b.name || "").toLowerCase();
          if (an !== bn) return an.localeCompare(bn);
          const as = (a.state || "").toLowerCase();
          const bs = (b.state || "").toLowerCase();
          return as.localeCompare(bs);
        });
        setCities(arr);
      })
      .catch(() => setCities([]))
      .finally(() => setCitiesLoading(false));
  }, []);

  useEffect(() => {
    if (!open) return;
    const el = messagesRef.current;
    if (!el) return;
    const t = window.requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    });
    return () => window.cancelAnimationFrame(t);
  }, [open, messages, loading, mode]);

  const step = STEPS[stepIndex];
  const isLastStep = step === "usingErp";
  const isSubmitStep = step === "submit";

  // Helper function to validate phone number
  const isValidPhone = (phone) => {
    const cleaned = phone.replace(/\D/g, '');
    return cleaned.length === 10;
  };

  const fetchBotReply = useCallback(
    async (nextStep, collected = {}) => {
      setLoading(true);
      try {
        const res = await fetch(API_ENDPOINTS.chatbotLanding, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            step: nextStep,
            collectedData: collected,
            userMessage: "",
          }),
        });
        const data = await res.json();
        const reply = data?.data?.reply || "";
        const text = (reply || "").trim() || WELCOME_MSG;
        setMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last?.role === "bot" && last?.text === text) return prev;
          return [...prev, { role: "bot", text }];
        });
      } catch {
        setMessages((prev) => [...prev, { role: "bot", text: WELCOME_MSG }]);
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const sendFreeChat = useCallback(async (messageText = null) => {
    const message = (messageText || chatInput).trim();
    if (!message || loading) return;
    setMessages((prev) => [...prev, { role: "user", text: message }]);
    setChatInput("");
    setLoading(true);
    setEscalation(null);
    setShowAllSuggestions(false);
    
    // Track user message count for lead capture prompt
    // Increment immediately and capture the new count
    const newMessageCount = freeChatUserMessageCount + 1;
    setFreeChatUserMessageCount(newMessageCount);
    
    // Check for pricing queries
    const pricingKeywords = ['price', 'pricing', 'cost', 'how much', 'fee', 'charge', 'plan', 'payment'];
    const isPricingQuery = pricingKeywords.some(keyword =>
      message.toLowerCase().includes(keyword)
    );

    if (isPricingQuery) {
      setMessages((prev) => [
        ...prev,
        {
          role: "bot",
          text: "Our pricing is customized based on the specific features and modules you need for your school. Our sales team will work with you to create a package that fits your requirements and budget. Would you like to schedule a demo to discuss pricing in detail?"
        }
      ]);
      setLoading(false);
      
      // Show lead capture prompt for pricing queries too
      if (newMessageCount >= 2 && !showLeadCapturePrompt && !formData.contactName) {
        setShowLeadCapturePrompt(true);
      }
      return;
    }
    
    // Check for specific feature queries
    const featureKeywords = ['bus tracking', 'bus', 'tracking', 'transport', 'gps', 'location', 'route'];
    const isFeatureQuery = featureKeywords.some(keyword =>
      message.toLowerCase().includes(keyword)
    );
    
    if (isFeatureQuery) {
      setMessages((prev) => [
        ...prev,
        {
          role: "bot",
          text: "Great question! Our School ERP system offers comprehensive features including student management, attendance tracking, fee collection, communication tools, and much more. We also provide customizations based on specific school requirements like bus tracking, biometric integration, and other specialized needs. Would you like to schedule a demo to see how we can customize the system for your school?"
        }
      ]);
      setLoading(false);
      
      // Show lead capture prompt for feature queries too
      if (newMessageCount >= 2 && !showLeadCapturePrompt && !formData.contactName) {
        setShowLeadCapturePrompt(true);
      }
      return;
    }
    
    // Get conversation context (last 5 message pairs)
    const conversationHistory = getConversationContext(5);
    
    const hasLeadSignal =
      !!formData.city ||
      !!formData.schoolName?.trim() ||
      !!formData.phoneNumber?.trim() ||
      !!formData.contactName?.trim();
    
    // Build lead context object
    const leadContext = hasLeadSignal ? {
      city: formData.city,
      schoolName: formData.schoolName,
      strengthOfSchool: formData.strengthOfSchool,
      designation: formData.designation
    } : null;
    
    if (hasLeadSignal) {
      upsertLead({
        city: formData.city,
        schoolName: formData.schoolName,
        phoneNumber: formData.phoneNumber,
        contactName: formData.contactName,
        designation: formData.designation,
        strengthOfSchool: formData.strengthOfSchool,
        isCurrentlyUsingErp: formData.isCurrentlyUsingErp,
        lastUserQuestion: message,
        lastStep: "free_chat",
      });
    }
    try {
      const res = await fetch(API_ENDPOINTS.publicChatAsk, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          message,
          conversationHistory,
          leadContext
        }),
      });
      const json = await res.json();
      let answer =
        (json?.data?.answer || "").trim() || "I don't have that information in the system.";
      const sources = Array.isArray(json?.data?.sources) ? json.data.sources : [];
      
      // Check for unhelpful responses and replace them with sales-friendly messages
      const unhelpfulPhrases = [
        "I don't have that information",
        "I don't have information",
        "not in the system",
        "don't have access",
        "unable to find",
        "no information available",
        "don't know",
        "can't find"
      ];
      
      const isUnhelpful = unhelpfulPhrases.some(phrase =>
        answer.toLowerCase().includes(phrase.toLowerCase())
      );
      
      if (isUnhelpful) {
        // Try to provide a topic-specific response based on the query
        const query = message.toLowerCase();
        
        if (query.includes('timetable') || query.includes('schedule')) {
          answer = `Our School ERP includes a powerful timetable management system that helps schools create, manage, and share schedules efficiently. Features include automated timetable generation, conflict detection, and easy modifications. Our team can show you a live demo and discuss how it can be customized for your school's needs. Would you like to schedule a demo?`;
        } else if (query.includes('fee') || query.includes('payment')) {
          answer = `Our system handles complete fee management including collection, tracking, online payments, and automated receipts. Our sales team can provide detailed information about the fee management module based on your school's specific requirements. Would you like to schedule a demo?`;
        } else if (query.includes('attendance')) {
          answer = `Our ERP includes comprehensive attendance tracking with biometric integration, automated SMS/email notifications to parents, and detailed reports. The exact features can be customized based on your needs. Our team can walk you through a live demo. Interested?`;
        } else if (query.includes('bus') || query.includes('transport') || query.includes('tracking')) {
          answer = `Great question! Our School ERP system includes comprehensive features for bus tracking and transport management. Each school's implementation is customized to their specific needs including GPS tracking, route management, and parent notifications. I'd recommend scheduling a demo with our team who can show you exactly how this works in practice and discuss your specific requirements. Would you like to schedule a demo?`;
        } else if (query.includes('library')) {
          answer = `Our library management module helps track books, issue/return processes, automated reminders, and catalog management. The system can be customized to fit your library's specific workflow. Our team can demonstrate the features in detail. Would you like to schedule a demo?`;
        } else if (query.includes('exam') || query.includes('test') || query.includes('assessment')) {
          answer = `Our examination module streamlines exam scheduling, paper generation, result processing, and report card generation with customizable grading systems. Each school can configure it according to their examination patterns. Our sales team can provide a detailed walkthrough. Would you like to schedule a demo?`;
        } else {
          // Generic helpful response for any other query
          answer = `That's a great question about ${message}! Our School ERP system is highly customizable and includes extensive features in this area. I'd recommend speaking with our sales team who can provide detailed information specific to your school's needs and show you a live demo. Would you like me to schedule a call?`;
        }
      }
      
      // Store bot message
      setMessages((prev) => [...prev, { role: "bot", text: answer }]);
      
      // Show lead capture prompt after 2 user interactions (1 actual question) if not already shown
      // User clicks "Ask Questions First" (count=1) + asks 1 question (count=2) = trigger prompt
      if (newMessageCount >= 2 && !showLeadCapturePrompt && !formData.contactName) {
        setShowLeadCapturePrompt(true);
      }
      
      const noInfo =
        answer.toLowerCase().includes("don't have that information") || sources.length === 0;
      if (noInfo) setEscalation({ question: message });
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "bot", text: "I couldn't fetch an answer right now. Please try again." },
      ]);
      setEscalation({ question: message });
    } finally {
      setLoading(false);
    }
  }, [chatInput, loading, upsertLead, formData, getConversationContext, freeChatUserMessageCount, showLeadCapturePrompt]);

  const goNext = useCallback((nextCollected = formData) => {
    if (stepIndex >= STEPS.length - 1) return;
    const nextIndex = stepIndex + 1;
    setStepIndex(nextIndex);
    const nextStep = STEPS[nextIndex];
    if (nextStep === "submit") {
      setMessages((prev) => [
        ...prev,
        { role: "bot", text: "Perfect! 🎉 Click the button below to get your free demo and we'll contact you soon." },
      ]);
      upsertLead({ ...nextCollected, lastStep: "submit" });
      return;
    }
    upsertLead({ ...nextCollected, lastStep: nextStep });
    fetchBotReply(nextStep, nextCollected);
  }, [stepIndex, formData, fetchBotReply, upsertLead]);

  const handleWelcomeAnswer = (choice) => {
    setTeaserVisible(false);
    if (choice === "form") {
      // User chose to fill the form first
      setMessages((prev) => [...prev, { role: "user", text: "📋 Fill Quick Form - Get Demo" }]);
      const nextIndex = STEPS.indexOf("city");
      setStepIndex(nextIndex);
      upsertLead({ lastStep: "city" });
      fetchBotReply("city", formData);
    } else if (choice === "chat") {
      // User chose to ask questions first
      setMode("free_chat");
      // Increment counter for the "Ask Questions First" interaction
      setFreeChatUserMessageCount(1);
      setMessages((prev) => [
        ...prev,
        { role: "user", text: "💬 Ask Questions First" },
        {
          role: "bot",
          text: "Great! I'm here to answer any questions about our School ERP system. You can ask me anything, or choose from the suggested questions below.",
        },
      ]);
    }
  };

  const handleScheduleDemo = () => {
    setShowLeadCapturePrompt(false);
    setMessages((prev) => [
      ...prev,
      { role: "user", text: "Yes, Schedule Demo" },
      {
        role: "bot",
        text: "Excellent! Let me gather a few details to schedule your personalized demo. 🎯",
      },
    ]);
    // Switch to lead flow mode and start from city step
    setMode("lead_flow");
    const nextIndex = STEPS.indexOf("city");
    setStepIndex(nextIndex);
    upsertLead({ lastStep: "city" });
    fetchBotReply("city", formData);
  };

  const dismissLeadCapture = () => {
    setShowLeadCapturePrompt(false);
    setMessages((prev) => [
      ...prev,
      { role: "user", text: "Ask Another Question" },
      {
        role: "bot",
        text: "No problem! Feel free to continue asking questions. I'm here to help! 😊",
      },
    ]);
  };

  const handleCitySelect = (city) => {
    const cityDisplay = `${city.name}${city.state ? `, ${city.state}` : ""}`;
    const nextCollected = { ...formData, city: city.name };
    setFormData(nextCollected);
    setSearchQuery(cityDisplay);
    setMessages((prev) => [...prev, { role: "user", text: cityDisplay }]);
    upsertLead({ ...nextCollected, lastStep: "city" });
    setShowDropdown(false);
    goNext(nextCollected);
  };

  const saveField = useCallback(
    (patch) => {
      upsertLead({ ...patch, lastStep: step });
    },
    [upsertLead, step]
  );

  const handleSubmitLead = async () => {
    setLoading(true);
    setSubmitStatus(null);
    try {
      await leadAction("submit", { ...formData, lastStep: "submit" });
      setSubmitStatus("success");
      setMessages((prev) => [
        ...prev,
        { role: "user", text: "Submitted" },
        { role: "bot", text: "Thank you! We'll get in touch with you soon." },
        {
          role: "bot",
          text: "If you have any doubts or questions, just ask me here and I'll try to help.",
        },
      ]);
      rotateLeadSession();
      setMode("free_chat");
    } catch {
      setSubmitStatus("error");
    } finally {
      setLoading(false);
    }
  };

  const handleFieldSubmit = (e) => {
    e.preventDefault();
    if (mode === "free_chat") {
      sendFreeChat();
      return;
    }
    if (isSubmitStep) {
      handleSubmitLead();
      return;
    }
    // Add the user's answer bubble for text steps
    if (step === "schoolName" && formData.schoolName?.trim()) {
      setMessages((prev) => [...prev, { role: "user", text: formData.schoolName.trim() }]);
      upsertLead({ schoolName: formData.schoolName.trim(), lastStep: "schoolName" });
    }
    if (step === "phone" && formData.phoneNumber?.trim()) {
      setMessages((prev) => [...prev, { role: "user", text: formData.phoneNumber.trim() }]);
      upsertLead({ phoneNumber: formData.phoneNumber.trim(), lastStep: "phone" });
    }
    if (step === "name" && formData.contactName?.trim()) {
      setMessages((prev) => [...prev, { role: "user", text: formData.contactName.trim() }]);
      upsertLead({ contactName: formData.contactName.trim(), lastStep: "name" });
    }
    if (step === "designation" && formData.designation?.trim()) {
      setMessages((prev) => [...prev, { role: "user", text: formData.designation.trim() }]);
      upsertLead({ designation: formData.designation.trim(), lastStep: "designation" });
    }
    goNext();
  };

  // Click outside handler for dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    if (showDropdown) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [showDropdown, setShowDropdown]);

  const canProceed = () => {
    switch (step) {
      case "city":
        return !!formData.city;
      case "schoolName":
        return !!formData.schoolName?.trim();
      case "phone":
        return !!formData.phoneNumber?.trim();
      case "name":
        return !!formData.contactName?.trim();
      case "designation":
        return !!formData.designation?.trim();
      case "strength":
        return !!formData.strengthOfSchool;
      case "usingErp":
        return formData.isCurrentlyUsingErp !== null;
      case "submit":
        return true;
      default:
        return false;
    }
  };

  if (!visible) return null;

 

  return (
    <>
      {open && <div className="fixed inset-0 bg-black/40 z-40" />}

    <div className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-2">
      <AnimatePresence>
        {teaserVisible && !open ? (
          <motion.div
            key="teaser"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            className="max-w-[280px] rounded-2xl rounded-br-md bg-white border border-gray-200 shadow-lg px-3 py-2 text-sm text-gray-800"
          >
            {WELCOME_MSG}
          </motion.div>
        ) : null}
      </AnimatePresence>

      <AnimatePresence>
        {open ? (
          <motion.div
            key="panel"
            initial={{ opacity: 0, y: 24, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.98 }}
            transition={{ type: "spring", stiffness: 260, damping: 22 }}
            className="w-[450px] max-w-[calc(100vw-2rem)] h-[600px] max-h-[calc(100vh-7rem)] rounded-2xl border border-gray-200 bg-white shadow-2xl overflow-hidden flex flex-col"
          >
            {/* Header with Mode Indicators */}
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="h-9 w-9 rounded-full bg-white/15 flex items-center justify-center ring-1 ring-white/20">
                  <span className="text-sm font-semibold">
                    {mode === "free_chat" ? "💬" : "🎯"}
                  </span>
                </div>
                <div className="leading-tight">
                  <div className="text-sm font-semibold">
                    {mode === "free_chat" ? "Ask Me Anything" : "Getting Started"}
                  </div>
                  <div className="text-xs text-white/80 flex items-center gap-1">
                    <span className="inline-block h-2 w-2 rounded-full bg-emerald-400" />
                    {mode === "free_chat" ? "AI Support Agent" : `Step ${stepIndex} of ${STEPS.length - 2}`}
                  </div>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="p-1 rounded hover:bg-white/20"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Progress Bar for Form Mode */}
            {mode === "lead_flow" && step !== "welcome" && step !== "submit" && (
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 px-4 py-3 border-b">
                <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                  <span className="font-medium">Step {stepIndex} of {STEPS.length - 2}</span>
                  <span>{Math.round((stepIndex / (STEPS.length - 2)) * 100)}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${(stepIndex / (STEPS.length - 2)) * 100}%` }}
                  />
                </div>
              </div>
            )}

            {/* Messages */}
            <div
              ref={messagesRef}
              className="flex-1 overflow-y-auto px-4 py-4 space-y-4 bg-gradient-to-b from-white to-gray-50"
            >
              {messages.map((m, idx) =>
                m.role === "bot" ? (
                  <div key={idx} className="flex gap-2 items-start">
                    <div className="h-8 w-8 rounded-full bg-blue-600/10 flex items-center justify-center text-blue-700 text-xs font-semibold ring-1 ring-blue-600/10 flex-shrink-0">
                      C
                    </div>
                    <div className="max-w-[85%]">
                      <div className={`rounded-2xl rounded-bl-md bg-white px-4 py-3 shadow-sm border ${
                        stepIndex > 0 && mode === "lead_flow" && idx === messages.length - 1
                          ? "text-base font-medium text-gray-800 border-l-4 border-blue-600"
                          : "text-sm text-gray-800 border-gray-100"
                      }`}>
                        {m.text}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div key={idx} className="flex justify-end">
                    <div className="max-w-[85%] rounded-2xl rounded-br-md bg-blue-600 px-3 py-2 text-sm text-white shadow-sm">
                      {m.text}
                    </div>
                  </div>
                )
              )}

              {mode === "lead_flow" && step === "welcome" && (
                <div className="flex gap-2 items-center pt-1">
                  <div className="h-8 w-8" />
                  <div className="flex flex-col gap-2 w-full max-w-[85%]">
                    <button
                      type="button"
                      onClick={() => handleWelcomeAnswer("form")}
                      className="rounded-xl border border-blue-600 bg-blue-600 px-4 py-3 text-sm font-semibold text-white shadow-sm hover:bg-blue-700 text-left"
                    >
                      📋 Fill Quick Form - Get Demo
                    </button>
                    <button
                      type="button"
                      onClick={() => handleWelcomeAnswer("chat")}
                      className="rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold hover:border-gray-300 hover:bg-gray-50 text-left"
                    >
                      💬 Ask Questions First
                    </button>
                  </div>
                </div>
              )}

              {mode === "lead_flow" && step === "designation" && (
                <div className="flex gap-2 items-center pt-2">
                  <div className="h-8 w-8" />
                  <div className="grid grid-cols-2 gap-2">
                    {DESIGNATION_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          setFormData((p) => ({
                            ...p,
                            designation: opt.value,
                          }));
                          setMessages((prev) => [...prev, { role: "user", text: opt.label }]);
                          upsertLead({ designation: opt.value, lastStep: "designation" });
                          setTimeout(goNext, 300);
                        }}
                        className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition ${
                          formData.designation === opt.value
                            ? "border-blue-600 bg-blue-50 text-blue-700"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {mode === "lead_flow" && step === "strength" && (
                <div className="flex gap-2 items-center pt-2">
                  <div className="h-8 w-8" />
                  <div className="grid grid-cols-2 gap-2">
                    {STRENGTH_OPTIONS.map((opt) => (
                      <button
                        key={opt.value}
                        type="button"
                        onClick={() => {
                          setFormData((p) => ({
                            ...p,
                            strengthOfSchool: opt.value,
                          }));
                          setMessages((prev) => [...prev, { role: "user", text: opt.label }]);
                          upsertLead({ strengthOfSchool: opt.value, lastStep: "strength" });
                          setTimeout(goNext, 300);
                        }}
                        className={`rounded-xl border px-3 py-2.5 text-sm font-semibold transition ${
                          formData.strengthOfSchool === opt.value
                            ? "border-blue-600 bg-blue-50 text-blue-700"
                            : "border-gray-200 hover:border-gray-300"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {mode === "lead_flow" && step === "usingErp" && (
                <div className="flex gap-2 items-center pt-2">
                  <div className="h-8 w-8" />
                  <div className="flex gap-2 w-full">
                    <button
                      type="button"
                      onClick={() => {
                        setFormData((p) => ({ ...p, isCurrentlyUsingErp: true }));
                        setMessages((prev) => [...prev, { role: "user", text: "Yes" }]);
                        upsertLead({ isCurrentlyUsingErp: true, lastStep: "usingErp" });
                        setTimeout(goNext, 300);
                      }}
                      className={`flex-1 rounded-xl border px-3 py-2.5 text-sm font-semibold ${
                        formData.isCurrentlyUsingErp === true
                          ? "border-blue-600 bg-blue-50 text-blue-700"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      Yes
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setFormData((p) => ({ ...p, isCurrentlyUsingErp: false }));
                        setMessages((prev) => [...prev, { role: "user", text: "No" }]);
                        upsertLead({ isCurrentlyUsingErp: false, lastStep: "usingErp" });
                        setTimeout(goNext, 300);
                      }}
                      className={`flex-1 rounded-xl border px-3 py-2.5 text-sm font-semibold ${
                        formData.isCurrentlyUsingErp === false
                          ? "border-blue-600 bg-blue-50 text-blue-700"
                          : "border-gray-200 hover:border-gray-300"
                      }`}
                    >
                      No
                    </button>
                  </div>
                </div>
              )}

              {mode === "free_chat" && messages.length <= 2 && !loading && (
                <div className="mt-4 space-y-2">
                  <div className="text-xs font-medium text-gray-500 px-1">
                    Suggested questions:
                  </div>
                  {SUGGESTED_QUESTIONS.slice(0, showAllSuggestions ? 6 : 3).map((question, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => {
                        setChatInput(question);
                        sendFreeChat(question);
                      }}
                      className="w-full text-left px-3 py-2 text-sm bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200"
                    >
                      {question}
                    </button>
                  ))}
                  {!showAllSuggestions && (
                    <button
                      onClick={() => setShowAllSuggestions(true)}
                      className="w-full text-center px-3 py-2 text-xs text-blue-600 hover:text-blue-700 font-medium"
                    >
                      See 3 more questions ▼
                    </button>
                  )}
                </div>
              )}

              {mode === "free_chat" && showLeadCapturePrompt && !escalation && (
                <>
                  <div className="flex gap-2 items-end">
                    <div className="h-8 w-8 rounded-full bg-blue-600/10 flex items-center justify-center text-blue-700 text-xs font-semibold ring-1 ring-blue-600/10">
                      C
                    </div>
                    <div className="max-w-[85%] rounded-2xl rounded-bl-md bg-blue-50 px-4 py-3 text-sm text-gray-800 shadow-sm border-l-4 border-blue-500">
                      <div className="font-medium mb-1">I'm glad I could help! 😊</div>
                      Would you like to schedule a personalized demo? I'll just need a few details.
                    </div>
                  </div>
                  
                  <div className="flex gap-2 items-center pt-2">
                    <div className="h-8 w-8" />
                    <div className="flex gap-2 w-full">
                      <Button
                        type="button"
                        className="flex-1 bg-blue-600 hover:bg-blue-700 rounded-xl h-11 text-sm font-semibold"
                        onClick={handleScheduleDemo}
                      >
                        Yes, Schedule Demo
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        className="flex-1 rounded-xl h-11 text-sm font-semibold"
                        onClick={dismissLeadCapture}
                      >
                        Ask Another Question
                      </Button>
                    </div>
                  </div>
                </>
              )}

              {mode === "free_chat" && escalation && (
                <>
                  <div className="flex gap-2 items-end">
                    <div className="h-8 w-8 rounded-full bg-blue-600/10 flex items-center justify-center text-blue-700 text-xs font-semibold ring-1 ring-blue-600/10">
                      C
                    </div>
                    <div className="max-w-[85%] rounded-2xl rounded-bl-md bg-amber-50 px-4 py-3 text-sm text-gray-800 shadow-sm border-l-4 border-amber-500">
                      <div className="font-medium mb-1">⚡ Need Expert Help?</div>
                      I'd be happy to connect you with our team for personalized assistance!
                    </div>
                  </div>
                  
                  <div className="flex gap-2 items-center pt-2">
                    <div className="h-8 w-8" />
                    <div className="grid grid-cols-2 gap-2 w-full">
                      <Button
                        type="button"
                        variant="outline"
                        className="rounded-xl h-11 text-sm font-semibold"
                        onClick={async () => {
                          setMessages((p) => [...p, { role: "user", text: "📞 Call Me Within 2 Hours" }]);
                          await leadAction("callback", { ...formData, lastStep: "free_chat" });
                          setMessages((p) => [
                            ...p,
                            { role: "bot", text: "Perfect! Our team will call you within 2 hours." },
                          ]);
                          setEscalation(null);
                          window.setTimeout(() => rotateLeadSession(), 800);
                        }}
                      >
                        📞 Call Me Within 2 Hours
                      </Button>
                      <Button
                        type="button"
                        className="bg-blue-600 hover:bg-blue-700 rounded-xl h-11 text-sm font-semibold"
                        onClick={async () => {
                          setMessages((p) => [...p, { role: "user", text: "📅 Book 30-Min Demo" }]);
                          await leadAction("demo", { ...formData, lastStep: "free_chat" });
                          setMessages((p) => [
                            ...p,
                            { role: "bot", text: "Excellent! We'll reach out to schedule your 30-minute demo." },
                          ]);
                          setEscalation(null);
                          window.setTimeout(() => rotateLeadSession(), 800);
                        }}
                      >
                        📅 Book 30-Min Demo
                      </Button>
                    </div>
                  </div>
                </>
              )}

              {loading ? (
                <div className="flex gap-2 items-end">
                  <div className="h-8 w-8 rounded-full bg-blue-600/10 flex items-center justify-center text-blue-700 text-xs font-semibold ring-1 ring-blue-600/10">
                    C
                  </div>
                  <div className="max-w-[85%] rounded-2xl rounded-bl-md bg-white px-3 py-2 text-sm text-gray-600 shadow-sm border border-gray-100">
                    <span className="flex items-center gap-2">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Typing…
                    </span>
                  </div>
                </div>
              ) : null}
            </div>

          
            <div className="border-t border-gray-100 bg-white px-4 py-3">
              <form onSubmit={handleFieldSubmit} className="space-y-3">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-xs font-medium text-gray-500">
                    {mode === "free_chat" ? "Ask anything" : "Quick enquiry"}
                  </div>
                  <button
                    type="button"
                    onClick={() => setMode((m) => (m === "free_chat" ? "lead_flow" : "free_chat"))}
                    className="text-xs font-semibold text-blue-700 hover:underline"
                  >
                    {mode === "free_chat" ? "Continue form" : "Ask a question"}
                  </button>
                </div>

                {mode === "free_chat" && (
                  <>
                    <div className="flex gap-2">
                      <Input
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        placeholder="Type your question..."
                        className="rounded-xl"
                      />
                      <Button
                        type="submit"
                        disabled={!chatInput.trim() || loading}
                        className="bg-blue-600 hover:bg-blue-700 rounded-xl h-11 px-4"
                      >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Send"}
                      </Button>
                    </div>
                  </>
                )}

                {mode === "free_chat" && !loading && messages.length > 1 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {QUICK_REPLIES.map((reply, idx) => (
                      <button
                        key={idx}
                        type="button"
                        onClick={() => {
                          setChatInput(reply.query);
                          sendFreeChat(reply.query);
                        }}
                        className="px-3 py-1.5 text-xs font-medium bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
                      >
                        {reply.label}
                      </button>
                    ))}
                  </div>
                )}

                {mode === "lead_flow" && (
                  <>
                {step === "city" && (
                  <div className="relative" ref={dropdownRef}>
                    <Label className="text-xs text-gray-600">City</Label>
                    <Input
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setShowDropdown(true);
                      }}
                      onFocus={() => setShowDropdown(true)}
                      placeholder="Search or select city..."
                      className="mt-1 rounded-xl"
                      autoComplete="off"
                      disabled={citiesLoading}
                    />
                    {showDropdown && filteredCities.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full max-h-[60vh] overflow-y-auto bg-white border border-gray-200 rounded-xl shadow-lg">
                        {filteredCities.map((city) => {
                          const isPopular = ["Mumbai", "Delhi", "Bangalore", "Hyderabad", "Chennai", "Pune"].includes(city.name);
                          return (
                            <button
                              key={city.id || city.name}
                              type="button"
                              onClick={() => handleCitySelect(city)}
                              className="w-full text-left px-3 py-2.5 text-sm hover:bg-gray-50 flex items-center gap-2 transition-colors"
                            >
                              {isPopular && <span className="text-yellow-500">⭐</span>}
                              <span>
                                {city.name}
                                {city.state ? `, ${city.state}` : ""}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {step === "schoolName" && (
                  <div>
                    <Label className="text-xs text-gray-600">School name</Label>
                    <Input
                      value={formData.schoolName}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, schoolName: e.target.value }))
                      }
                      onBlur={() => {
                        const v = (formData.schoolName || "").trim();
                        if (v) saveField({ schoolName: v });
                      }}
                      placeholder="Your school name"
                      className="mt-1 rounded-xl"
                    />
                    <div className="mt-1 text-xs text-gray-500 px-1">
                      💡 Helps us personalize your experience
                    </div>
                  </div>
                )}

                {step === "phone" && (
                  <div>
                    <Label className="text-xs text-gray-600">Phone number</Label>
                    <Input
                      type="tel"
                      value={formData.phoneNumber}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, phoneNumber: e.target.value }))
                      }
                      onBlur={() => {
                        const v = (formData.phoneNumber || "").trim();
                        if (v) saveField({ phoneNumber: v });
                      }}
                      placeholder="Your phone number"
                      className="mt-1 rounded-xl"
                    />
                    <div className="mt-1 text-xs text-gray-500 px-1">
                      💡 We'll use this to send you demo details
                    </div>
                    {formData.phoneNumber && !isValidPhone(formData.phoneNumber) && (
                      <div className="mt-2 text-xs text-red-600 px-1">
                        ⚠️ Please enter a valid 10-digit mobile number
                      </div>
                    )}
                  </div>
                )}

                {step === "name" && (
                  <div>
                    <Label className="text-xs text-gray-600">Your name</Label>
                    <Input
                      value={formData.contactName}
                      onChange={(e) =>
                        setFormData((p) => ({ ...p, contactName: e.target.value }))
                      }
                      onBlur={() => {
                        const v = (formData.contactName || "").trim();
                        if (v) saveField({ contactName: v });
                      }}
                      placeholder="Your name"
                      className="mt-1 rounded-xl"
                    />
                    <div className="mt-1 text-xs text-gray-500 px-1">
                      💡 For receiving pricing information
                    </div>
                  </div>
                )}

                {["schoolName", "phone", "name", "designation", "submit"].includes(step) && (
                  <Button
                    type="submit"
                    disabled={!canProceed() || loading}
                    className="w-full bg-blue-600 hover:bg-blue-700 rounded-xl h-11 font-semibold"
                  >
                    {loading ? (
                      <span className="flex items-center gap-2">
                        <Loader2 className="h-4 w-4 animate-spin" />
                        {isSubmitStep ? "📤 Submitting..." : "Processing..."}
                      </span>
                    ) : isSubmitStep ? (
                      "🎉 Get Your Free Demo"
                    ) : (
                      "Next →"
                    )}
                  </Button>
                )}

                {step === "usingErp" && formData.isCurrentlyUsingErp !== null && (
                  <Button
                    type="button"
                    onClick={() => {
                      setMessages((prev) => [
                        ...prev,
                        { role: "user", text: formData.isCurrentlyUsingErp ? "Yes" : "No" },
                      ]);
                      upsertLead({ isCurrentlyUsingErp: formData.isCurrentlyUsingErp, lastStep: "usingErp" });
                      goNext();
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700 rounded-xl h-11"
                  >
                    Next
                  </Button>
                )}
                  </>
                )}
              </form>

              {submitStatus === "success" && (
                <p className="text-sm text-green-600 font-medium">
                  Submitted! We'll contact you soon.
                </p>
              )}
              {submitStatus === "error" && (
                <p className="text-sm text-red-600">
                  Failed to send. Please try again or use the contact form.
                </p>
              )}
            </div>
          </motion.div>
        ) : null}
      </AnimatePresence>

      <div className="flex items-center gap-3">
        <motion.button
          type="button"
          onClick={() => window.open("https://wa.me/917499070944?text=Hi%2C%20I%20would%20like%20to%20book%20a%20demo%20for%20Clynox", "_blank")}
          className="flex h-14 w-14 items-center justify-center rounded-full bg-green-600 text-white shadow-xl hover:bg-green-700 transition ring-1 ring-black/5"
          aria-label="Open WhatsApp"
          title="WhatsApp"
        >
          <svg viewBox="0 0 24 24" className="h-7 w-7 fill-current" aria-hidden="true">
            <path d="M12.04 2C6.49 2 2 6.49 2 12.04c0 1.75.46 3.45 1.33 4.95L2 22l5.16-1.31c1.44.79 3.06 1.2 4.88 1.2 5.55 0 10.04-4.49 10.04-10.04S17.59 2 12.04 2Zm0 18.33c-1.54 0-3.02-.41-4.3-1.18l-.31-.18-3.06.78.82-2.98-.2-.31a8.2 8.2 0 0 1-1.25-4.39c0-4.54 3.69-8.23 8.23-8.23s8.23 3.69 8.23 8.23-3.69 8.23-8.16 8.23Zm4.76-6.4c-.26-.13-1.53-.76-1.77-.85-.24-.09-.41-.13-.59.13-.18.26-.68.85-.83 1.03-.15.18-.31.2-.57.07-.26-.13-1.09-.4-2.07-1.28-.76-.68-1.28-1.52-1.43-1.77-.15-.26-.02-.4.11-.53.11-.11.26-.31.39-.46.13-.15.17-.26.26-.44.09-.18.04-.33-.02-.46-.07-.13-.59-1.42-.81-1.95-.21-.5-.43-.43-.59-.44l-.5-.01c-.18 0-.46.07-.7.33-.24.26-.92.89-.92 2.18s.94 2.54 1.07 2.72c.13.18 1.86 2.84 4.51 3.98.63.27 1.12.43 1.5.55.63.2 1.2.17 1.65.1.5-.08 1.53-.62 1.75-1.22.22-.61.22-1.13.15-1.24-.07-.11-.24-.18-.5-.31Z" />
          </svg>
        </motion.button>

        <motion.button
          type="button"
          onClick={() => {
            setTeaserVisible(false);
            setOpen((o) => !o);
          }}
          className="flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-xl hover:bg-blue-700 transition ring-1 ring-black/5"
          aria-label="Open ERP bot"
        >
          <MessageCircle className="h-7 w-7" />
        </motion.button>
      </div>
    </div>
    </>
  );
}
